/**
 * loadThinning.js — какие серии загружены ПРОРЕЖЕННЫМИ.
 *
 * ЗАЧЕМ ЭТО ОТДЕЛЬНЫМ ФАЙЛОМ. Страж-прореживатель спрашивает про большой
 * каталог ДО того, как программа узнала, из каких серий он состоит: на этом
 * шаге есть только список файлов. Значит «каждый 2-й срез» режет все серии
 * разом — включая ту тонкосрезную, по которой врач мерит объём очага.
 *
 * Само прореживание по сериям — работа отдельная (и, скорее всего, ненужная:
 * объём для 3D/MPR и так прореживается пер-серийно в sliceBudget.js, а стопка
 * при этом сохраняет все срезы). А вот что обязательно: ЧИСЛО, посчитанное на
 * прореженной серии, не должно приходить к врачу молча.
 *
 * ЦЕНА ПРОРЕЖИВАНИЯ, ИЗМЕРЕННАЯ, А НЕ ПРЕДПОЛОЖЕННАЯ. Шар известного объёма,
 * измеренный при разном положении сетки срезов относительно очага (фаза):
 *
 *   очаг ⌀6 мм:  срез 1 мм → разброс 6%,   срез 2 мм → разброс 21%
 *   очаг ⌀9 мм:  срез 1 мм → разброс 3%,   срез 2 мм → разброс 6%
 *   очаг ⌀12 мм: срез 1 мм → разброс 1%,   срез 2 мм → разброс 4%
 *
 * Прореживание не занижает объём ровно и предсказуемо — оно втрое-вчетверо
 * увеличивает СЛУЧАЙНУЮ ошибку, и у двух исследований фаза своя. На очаге
 * 6 мм это даёт до 23% «роста» или «убыли» из ничего — то есть ровно в том
 * числе, по которому решают, растёт ли опухоль.
 *
 * Файл без импортов: правило проверяется тестом, а не запуском программы.
 */

/** Прореживание, о котором уже известно, но серии ещё не разобраны. */
let _ждёт = null;          // { шаг, взято, всего }
/** seriesUID → { шаг, взято, всего } */
const _серии = new Map();
/** Серии, о которых уже известно, что они загружены ЦЕЛИКОМ. */
const _чистые = new Set();

/**
 * Запомнить, что следующая загрузка идёт с прореживанием.
 * Зовётся из стража-прореживателя — с ОБОИХ путей загрузки.
 */
export function notePendingThinning({ шаг, взято, всего } = {}) {
  const s = Math.max(2, Math.round(Number(шаг) || 0) || 2);
  _ждёт = { шаг: s, взято: Number(взято) || 0, всего: Number(всего) || 0 };
  return _ждёт;
}

/**
 * Серии загружены ЦЕЛИКОМ — запомнить, чтобы следующая прореженная загрузка
 * не приписала им своего прореживания.
 *
 * Зачем отдельно. Список серий, который приходит в конце загрузки, —
 * НАКОПЛЕННЫЙ: там и то, что загрузили пять минут назад. Первая версия этой
 * пометки из-за того и ошиблась: прореживание второго каталога легло заодно на
 * первое исследование, загруженное целиком (поймано проверкой браузером).
 */
export function noteWholeSeries(seriesUIDs) {
  for (const uid of seriesUIDs || []) {
    if (!uid) continue;
    _чистые.add(String(uid));
    _серии.delete(String(uid));
  }
}

/**
 * Серии разобраны — привязать к ним ожидающую пометку.
 * Зовётся из fileOps в конце загрузки.
 *
 * @param {string[]} seriesUIDs серии этой загрузки (или весь накопленный
 *   список — тогда заведомо целые серии отфильтруются сами)
 */
export function attachPendingThinning(seriesUIDs) {
  if (!_ждёт) return 0;
  let n = 0;
  for (const uid of seriesUIDs || []) {
    if (!uid) continue;
    const ключ = String(uid);
    if (_чистые.has(ключ)) continue;   // эта серия загружалась целиком — не наша
    _серии.set(ключ, { ..._ждёт });
    n++;
  }
  _ждёт = null;
  return n;
}

/** Загружена ли эта серия прореженной, и с каким шагом. */
export function thinningOfSeries(seriesUID) {
  return _серии.get(String(seriesUID || '')) || null;
}

/**
 * Одна строка для врача — или пустая, если резать ничего не резали.
 * Строка приписывается к ЧИСЛУ (объём очага, карта разницы), а не только к
 * моменту загрузки: тост при загрузке через месяц никто не вспомнит.
 *
 * @param {string[]} seriesUIDs серии, по которым посчитано число
 */
export function thinningWarning(seriesUIDs) {
  const шаги = new Set();
  for (const uid of seriesUIDs || []) {
    const т = thinningOfSeries(uid);
    if (т) шаги.add(т.шаг);
  }
  if (!шаги.size) return '';
  const шаг = Math.max(...шаги);
  const порядковый = шаг === 2 ? 'каждый 2-й' : `каждый ${шаг}-й`;
  return `⚠ загружено с прореживанием (${порядковый} срез по всему каталогу): `
    + 'шаг между срезами больше настоящего, и это число несёт добавочную ошибку '
    + '(на очаге около 6 мм — до 20%). Для измерения загрузите каталог целиком';
}

/** Всё, что известно о прореживании — наружу для проверок и разбора. */
export function thinningSnapshot() {
  return {
    ждёт: _ждёт ? { ..._ждёт } : null,
    серий: _серии.size,
    серии: [..._серии.entries()].map(([uid, т]) => ({ uid, ...т })),
  };
}

/** Забыть всё (новая загрузка «целиком», очистка сеанса). */
export function clearThinning(seriesUIDs) {
  if (!seriesUIDs) { _серии.clear(); _чистые.clear(); _ждёт = null; return; }
  for (const uid of seriesUIDs) _серии.delete(String(uid));
}

export default {
  notePendingThinning, attachPendingThinning, noteWholeSeries, thinningOfSeries,
  thinningWarning, thinningSnapshot, clearThinning,
};
